<template>
  <div :class="b({ favorite: location.favorite })">
    <component :is="location.url || locationWebUrl ? 'a' : 'span'"
               :class="b('name', { linked: !!location.url || !!locationWebUrl })"
               :href="location.url ? addContextPathToUrl(location.url) : locationWebUrl"
               :target="locationWebUrl && !location.url ? '_blank' : undefined"
               :rel="locationWebUrl && !location.url ? 'noopener noreferrer' : undefined"
    >
      <h4 :class="b('name-wrapper')">
        {{ location.displayName }}
        <e-icon v-if="location.url" icon="i-arrow-2--right" size="18" />
        <e-icon v-else-if="locationWebUrl" icon="i-external-link" size="18" />
      </h4>
    </component>

    <div :class="b('wrapper-address')">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-html="location.address?.formattedAddress"></div>
      <div :class="b('contact')">
        <div v-if="location.address?.phone" :class="b('icon-wrapper')">
          <e-icon icon="i-phone" size="14" />
          {{ $t('global.phoneAbbreviation') }} {{ location.address?.phone }}
        </div>
        <div v-if="location.address?.fax" :class="b('icon-wrapper')">
          <e-icon icon="i-phone" size="14" />
          {{ $t('global.faxAbbreviation') }} {{ location.address?.fax }}
        </div>
        <div v-if="location.address?.cellphone" :class="b('icon-wrapper')">
          <e-icon icon="i-phone" size="14" />
          {{ $t('global.mobilePhoneAbbreviation') }} {{ location.address?.cellphone }}
        </div>
      </div>
    </div>

    <a v-if="location?.url"
       :href="addContextPathToUrl(location.url)"
       :class="b('location-link')"
    >
      <span :class="b('icon-wrapper')">
        {{ $t('c-location-tile.moreInformationLabel') }}
        <e-icon icon="i-arrow--right" size="14" />
      </span>
    </a>
    <div v-if="showOpeningTimes && location.todayOpeningTime" :class="b('opening-hours-wrapper')">
      <h5 :class="b('opening-hours-title')">
        {{ $t('c-location-tile.todaysOpeningHoursTitle') }}
      </h5>
      {{ getFormattedOpeningHours(location.todayOpeningTime) }}
    </div>
    <button
      v-if="sessionStore.flags.storeSelectionEnabled"
      :aria-label="
        $t(location.favorite ? 'c-google-maps-info-window.removeAsFavorite' : 'c-google-maps-info-window.setAsFavorite')
      "
      :class="b('toggle-favorite-button')"
      type="button"
      @click="$emit('click', location)"
    >
      <!-- eslint-disable-next-line vue/max-len -->
      {{ $t(location.favorite ? 'c-location-tile.removeAsFavoriteLocation' : 'c-location-tile.setAsFavoriteLocation') }}
      <e-icon :icon="location.favorite ? 'i-close' : 'i-pin'" :size="location.favorite ? '18' : '20'" />
    </button>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from 'vue';
  import useSessionStore from '@/stores/session';
  import eIcon from '@/elements/e-icon.vue';
  import addContextPathToUrl from '@/helpers/add-context-path-to-url';
  import getFormattedOpeningHours from '@/helpers/opening-hours';
  import { PointOfService } from '@/types/point-of-service';

  type Setup = {
    getFormattedOpeningHours: typeof getFormattedOpeningHours;
    addContextPathToUrl: typeof addContextPathToUrl;
    sessionStore: ReturnType<typeof useSessionStore>;
  }

  // type Data = {}

  /**
   * Renders a location item with name, address, contact and opening hours.
   */
  export default defineComponent({
    name: 'c-location-tile',

    components: {
      eIcon,
    },

    props: {
      /**
       * Expects an location object.
       */
      location: {
        type: Object as PropType<PointOfService>,
        required: true,
      },
    },
    emits: {
      click: (payload: PointOfService) => !!payload,
    },

    setup(): Setup {
      return {
        getFormattedOpeningHours,
        addContextPathToUrl,
        sessionStore: useSessionStore(),
      };
    },
    // data(): Data {},

    computed: {
      locationWebUrl(): string | undefined {
        const webUrl = this.location.address?.webUrl;

        if (!webUrl) {
          return undefined;
        }

        if (!webUrl.startsWith('http://') && !webUrl.startsWith('https://')) {
          const absoluteWebUrl = webUrl.startsWith('www.')
            ? `https://${webUrl.substring(4)}`
            : `https://${webUrl}`;

          return absoluteWebUrl;
        }

        return webUrl;
      },

      showOpeningTimes(): boolean {
        return import.meta.env.VITE_STORE_FINDER_SHOW_OPENING_TIMES === '1';
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    // methods: {},
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .c-location-tile {
    $this: &;

    display: grid;
    grid-template-areas:
      'name'
      'address'
      'opening-hours'
      'location-link'
      'pin';
    gap: variables.$spacing--10;
    padding-bottom: variables.$spacing--20;
    border-bottom: 4px solid variables.$color-grayscale--0;

    @include mixins.media(lg) {
      grid-template-areas:
        'name name'
        'address pin'
        'location-link opening-hours';
      gap: variables.$spacing--20 variables.$spacing--40;
    }

    &__wrapper-address {
      @include mixins.hyphens();

      display: flex;
      grid-area: address;
      gap: variables.$spacing--10;
      flex-direction: column;

      @include mixins.media(md) {
        gap: variables.$spacing--20;
      }
    }

    &__name {
      grid-area: name;

      &--linked {
        &:hover,
        &:focus {
          color: variables.$color-primary--1;
        }
      }
    }

    &__location-link {
      grid-area: location-link;
      place-self: end end;

      @include mixins.media(lg) {
        justify-self: start;
      }

      &:hover,
      &:focus {
        color: variables.$color-primary--1;
      }
    }

    &__toggle-favorite-button {
      display: flex;
      grid-area: pin;
      gap: variables.$spacing--10;
      align-items: center;
      margin-bottom: auto;
      cursor: pointer;
      text-align: right;
      justify-self: end;

      &:hover,
      &:focus {
        color: variables.$color-primary--1;
      }
    }

    &__opening-hours-title {
      @include mixins.font(variables.$font-size--16, variables.$line-height--18, variables.$font-weight--bold);

      margin-bottom: variables.$spacing--5;
    }

    &__opening-hours-wrapper {
      grid-area: opening-hours;
      align-self: end;

      @include mixins.media(lg) {
        text-align: right;
      }
    }

    &__icon-wrapper,
    &__name-wrapper {
      display: flex;
      gap: variables.$spacing--10;
      align-items: center;

      @include mixins.media(md) {
        align-items: flex-start;
      }

      @include mixins.media(lg) {
        align-items: center;
      }
    }

    &__name-wrapper {
      justify-content: space-between;
      margin-bottom: 0;

      @include mixins.media(md) {
        justify-content: flex-start;
      }
    }

    &__contact {
      display: flex;
      gap: variables.$spacing--5;
      flex-direction: column;
    }

    &--favorite {
      @include mixins.media(md) {
        #{$this}__toggle-favorite-button {
          align-items: flex-start;

          .e-icon {
            margin-top: variables.$spacing--5;
          }
        }
      }
      #{$this}__name-link {
        color: variables.$color-primary--1;
      }
    }
  }
</style>
